import { useRef, useState } from 'react'

import {
  Alert,
  Box,
  Button,
  InputCheckBox,
  Text,
  TogglerCheckbox,
} from '@factorialco/deprecated-design-system'
import { RichTextEditorHandle } from '@factorialco/f0-react/dist/experimental'
import { Features } from 'generated/featureDefinitions'
import {
  AtsCandidateEmailActionDelaysEnum,
  RejectApplicationsMutationVariables,
  RejectableApplicationFragment,
} from 'generated/resources/graphql'

import DropdownSearchBar from 'modules/core/components/DropdownSearchBar'
import InformationTooltip from 'modules/core/components/Inputs/InformationTooltip'
import Modal from 'modules/core/components/Modal'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useEffect } from 'modules/core/hooks/useEffect'
import { useFeatureAvailability } from 'modules/core/hooks/useFeatureAvailability'
import { FormErrors, useForm } from 'modules/core/hooks/useForm'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { DelayMessageSelector } from 'modules/ats/components/MessageBoard/NewMessage/DelayMessageSelector'
import { SendMessageButton } from 'modules/ats/components/SendMessageButton'
import { TextEditorWithMessageTemplates } from 'modules/ats/components/TextEditorWithMessageTemplates'
import { useRejectApplications } from 'modules/ats/hooks/applications/useRejectApplications'
import { useCandidateEmailActions } from 'modules/ats/hooks/candidateEmailActions/useCandidateEmailActions'
import { useMessageTemplatesFeatureAvailability } from 'modules/ats/hooks/features/useMessageTemplatesFeatureAvailability'
import { useRejectionReasons } from 'modules/ats/hooks/rejectionReasons/useRejectionReasons'
import { getDelayTime, sendAsCorporateEmail } from 'modules/ats/lib/utils/candidateEmailActions'

import { RejectSmartActionPreview } from './RejectSmartActionPreview'

interface Props {
  jobPostingId: number
  selectedApplications: RejectableApplicationFragment[]
  title: string
  saveToTalentPoolCheckboxVisible: boolean
  saveToTalentPoolCheckboxValue: boolean
  onClose: () => void
  onSuccess: () => void
}

export const RejectApplicationsModal = ({
  selectedApplications,
  title,
  jobPostingId,
  onClose,
  onSuccess,
  saveToTalentPoolCheckboxVisible,
  saveToTalentPoolCheckboxValue,
}: Props) => {
  const richTextEditorRef = useRef<RichTextEditorHandle>(null)
  const { loading: rejecting, rejectApplications } = useRejectApplications()
  const hasMessageTemplatesFeature = useMessageTemplatesFeatureAvailability()
  const { reasons, loading } = useRejectionReasons()
  const applicationPhaseId = selectedApplications[0]?.applicationPhase.id
  const { candidateEmailActions, loading: loadingActions } = useCandidateEmailActions({
    atsJobPostingId: jobPostingId,
    enabled: true,
    actionType: 'reject',
    atsApplicationPhaseIds: applicationPhaseId ? [applicationPhaseId] : [],
  })
  const { featureAvailability } = useFeatureAvailability(Features.ats_smart_actions)
  const smartActionsActive = featureAvailability.available
  const action = candidateEmailActions ? candidateEmailActions[0] : undefined

  const applicationPhase = action?.atsApplicationPhases.find(
    (phase) => phase.id === applicationPhaseId
  )
  const populateAction = smartActionsActive && !!action

  const getMessageDelay = (delay?: AtsCandidateEmailActionDelaysEnum | null) => {
    if (!delay || delay === 'now') return null

    return getDelayTime(delay)
  }

  const form = useForm<RejectApplicationsMutationVariables>({
    defaultValues: {
      ids: [],
      atsRejectionReasonId: -1,
      disqualifiedMessage: populateAction ? action?.messageContent : '',
      sendAsCorporateEmail: populateAction ? sendAsCorporateEmail(action.emailFrom) : true,
      talentPool: saveToTalentPoolCheckboxValue,
      messageDelayedUntil: populateAction ? action.delay : null,
      notify: false,
    },
    validate(values) {
      const errors: FormErrors<RejectApplicationsMutationVariables> = {}

      if (values.atsRejectionReasonId < 0) {
        errors.disqualifiedMessage = i18n.t('common.validations.empty')
      }

      return errors
    },
    onSubmit: async (data, { setErrors }) => {
      const result = await rejectApplications({
        ids: selectedApplicationIds,
        disqualifiedMessage: data.disqualifiedMessage,
        sendAsCorporateEmail: data.sendAsCorporateEmail,
        notify: data.notify,
        talentPool: data.talentPool,
        messageDelayedUntil: getMessageDelay(data.messageDelayedUntil),
        atsRejectionReasonId: data.atsRejectionReasonId,
      })

      const handled = handleResult(result, (errors) => {
        setErrors(errors)
      })

      if (handled.success) {
        handleTracking()
        onSuccess()

        app.setFlash({
          type: 'correct',
          message: i18n.t('ats.candidates.applications.disqualify.modal.success_message'),
        })
      }

      return handled
    },
  })

  useEffect(() => {
    form.setFieldValue('talentPool', saveToTalentPoolCheckboxValue)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saveToTalentPoolCheckboxValue])

  useEffect(() => {
    if (populateAction) {
      form.setFieldValue('disqualifiedMessage', action?.messageContent)
      form.setFieldValue('sendAsCorporateEmail', sendAsCorporateEmail(action.emailFrom))
      form.setFieldValue('messageDelayedUntil', action.delay)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingActions])

  const submitAndNotify = async () => {
    await form.setFieldValue('notify', true)
    form.submitForm()
  }

  const selectedApplicationIds = selectedApplications.map((application) => application.id)

  const someApplicationsHasNoConsentToTalentPool = selectedApplications.some(
    (application) => !application.candidate?.consentToTalentPool
  )

  const applicationsWithoutEmail = selectedApplications.filter(
    (application) => !application.candidate?.email
  ).length

  const hasApplicationWithEmail = applicationsWithoutEmail !== selectedApplications.length
  const canSendCustomMessage = hasMessageTemplatesFeature && hasApplicationWithEmail
  const [editMessage, setEditMessage] = useState(false)
  const { company } = useCurrentSession()

  const handleTracking = () => {
    if (!smartActionsActive) return

    if (editMessage) {
      track(trackingEvents.ats.smartActionEdit, {
        company_id: company.id,
        stage_id: [applicationPhase?.hiringStage?.id],
        stage_name: [applicationPhase?.hiringStage?.name || 'Unassigned'],
        job_position: jobPostingId,
        action_type: 'reject',
      })
    } else {
      track(trackingEvents.ats.smartActionPerform, {
        company_id: company.id,
        stage_id: [applicationPhase?.hiringStage?.id],
        stage_name: [applicationPhase?.hiringStage?.name || 'Unassigned'],
        job_position: jobPostingId,
        action_type: 'reject',
      })
    }
  }

  if (!selectedApplications.length) return null

  const information = () => {
    if (selectedApplications?.length === 1) {
      const candidateName = selectedApplications[0]?.candidate?.fullName
      const params = {
        candidate_name: candidateName,
      }

      return someApplicationsHasNoConsentToTalentPool
        ? i18n.t(
            'ats.candidates.applications.disqualify.modal.talent_pool_negative_consent',
            params
          )
        : i18n.t(
            'ats.candidates.applications.disqualify.modal.talent_pool_affirmative_consent',
            params
          )
    } else {
      return someApplicationsHasNoConsentToTalentPool
        ? i18n.t('ats.candidates.applications.disqualify.modal.bulk_consent_negative')
        : i18n.t('ats.candidates.applications.disqualify.modal.bulk_consent_affirmative')
    }
  }

  return (
    <Modal
      illustration="user-move"
      title={title}
      description={i18n.tp('ats.candidates.applications.disqualify.modal.description', {
        count: selectedApplicationIds.length,
      })}
      onClose={onClose}
      onSubmit={form.submitForm}
      footer={
        canSendCustomMessage ? (
          <>
            <Button
              disabled={rejecting || form.values.atsRejectionReasonId < 0}
              type="secondary"
              onClick={form.submitForm}
            >
              {i18n.t('ats.candidates.applications.disqualify.modal.dont_sent_message')}
            </Button>
            {smartActionsActive ? (
              <Button
                disabled={rejecting || !form.isValid || !form.values.disqualifiedMessage}
                onClick={submitAndNotify}
              >
                {i18n.t('ats.candidates.applications.disqualify.modal.disqualify')}
              </Button>
            ) : (
              <SendMessageButton
                label={i18n.t('ats.candidates.applications.disqualify.modal.disqualify')}
                disabled={rejecting || !form.isValid}
                onSendMessage={submitAndNotify}
                onSendDelayedMessage={(when: string) => {
                  form.setFieldValue('messageDelayedUntil', when)
                  submitAndNotify()
                }}
              />
            )}
          </>
        ) : (
          <Button disabled={rejecting || !form.isValid} submit>
            {i18n.t('ats.candidates.applications.disqualify.modal.disqualify')}
          </Button>
        )
      }
    >
      <Box gap="s24">
        <Box gap="s4">
          <Box flexDirection="row" gap="s8" alignItems="center">
            <Text.Normal size="200" weight="medium" color="grey1000">
              {i18n.t('ats.candidates.applications.disqualify.modal.reason_to_disqualify')}
            </Text.Normal>
            <InformationTooltip
              information={i18n.t(
                'ats.candidates.applications.disqualify.modal.rejection_information'
              )}
              icon="question"
            />
          </Box>
          <DropdownSearchBar
            data={reasons.map((reason) => ({
              label: reason.reason,
              value: reason.id,
            }))}
            name={
              reasons.filter((reason) => reason.id === form.values.atsRejectionReasonId)[0]
                ?.reason ||
              i18n.t('ats.candidates.applications.disqualify.modal.select_placeholder')
            }
            onSelect={(reason) => form.setFieldValue('atsRejectionReasonId', reason.value)}
            labelNotFound={i18n.t('ats.candidates.applications.disqualify.modal.no_search_match')}
            label={''}
            searchLabel={i18n.t('common.search_by')}
            selectLabel={i18n.t('common.select')}
            sortLabel={i18n.t('ats.candidates.applications.disqualify.modal.reason')}
            disabled={loading}
          />
        </Box>
        {saveToTalentPoolCheckboxVisible && (
          <Box flexDirection="row" gap="s4" alignItems="center">
            <Box>
              <InputCheckBox
                checked={form.values.talentPool ?? false}
                onClick={() => form.setFieldValue('talentPool', !form.values.talentPool)}
              />
            </Box>
            <Text.Normal>
              {i18n.t('ats.candidates.applications.disqualify.modal.save_candidate_talent_pool')}
            </Text.Normal>
            <InformationTooltip information={information()} />
          </Box>
        )}
        {canSendCustomMessage &&
          !loadingActions &&
          (populateAction && !editMessage ? (
            <RejectSmartActionPreview
              candidateEmailAction={action}
              onEdit={() => setEditMessage(!editMessage)}
            />
          ) : (
            <Box gap="s16">
              <TextEditorWithMessageTemplates
                richTextEditorRef={richTextEditorRef}
                onChange={(value) => form.setFieldValue('disqualifiedMessage', value)}
                placeholder={i18n.t('ats.candidates.applications.disqualify.modal.placeholder')}
                value={form.values.disqualifiedMessage || ''}
                showLabel
              />

              <TogglerCheckbox
                value={form.values.sendAsCorporateEmail ?? false}
                onChange={(sendAsCorporateEmailValue) =>
                  form.setFieldValue('sendAsCorporateEmail', sendAsCorporateEmailValue)
                }
                label={i18n.t('ats.candidates.applications.disqualify.modal.send_as_corporate')}
                information={i18n.t(
                  'ats.candidates.applications.disqualify.modal.send_as_corporate_tooltip'
                )}
              />
              {smartActionsActive && (
                <DelayMessageSelector
                  label={i18n.t('ats.smart_actions.sidepanel.delay')}
                  value={form.values.messageDelayedUntil || ''}
                  onChange={(messageDelayedUntil) =>
                    form.setFieldValue('messageDelayedUntil', messageDelayedUntil)
                  }
                />
              )}
            </Box>
          ))}
        {applicationsWithoutEmail > 0 ? (
          <Alert color="warning">
            {i18n.t('ats.candidates.applications.bulk_disqualify.applications_without_email', {
              count: applicationsWithoutEmail,
            })}
          </Alert>
        ) : null}
      </Box>
    </Modal>
  )
}
